import {BUTTONS_STYLES_OPTIONS, type Template} from '@chekin/core';
import {deriveGuestSurface, type GuestSurfaceRoles} from '@chekinapp/ui';

export type BrandingVariables = Record<string, string | undefined>;

const KIT_GUEST_FONT_FALLBACK =
  "Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export function toQuotedFontFamily(family: string) {
  const escaped = family
    .replace(/[\r\n]+/g, ' ')
    .replace(/[\\']/g, match => `\\${match}`)
    .trim();
  return `'${escaped}'`;
}

function toSurfaceRoles(template: Template, fontsReady: boolean): GuestSurfaceRoles {
  const family = template.body_font?.font_name || template.titles_font?.font_name;

  return {
    brand: template.brand_primary_color,
    icons: template.brand_icons_color,
    ink: template.font_primary_color,
    inkSecondary: template.font_secondary_color,
    page: template.background_border_main_color,
    border: template.background_border_secondary_color,
    card: template.background_border_boxes_and_modals_color,
    buttonPrimaryBg: template.main_button_background_color,
    buttonSecondary: template.secondary_button_text_and_borders_color,
    buttonLink: template.text_button_text_color,
    navBg: template.navigation_background_color,
    navItemText: template.navigation_menu_items_text_color,
    fontFamily:
      fontsReady && family
        ? `${toQuotedFontFamily(family)}, ${KIT_GUEST_FONT_FALLBACK}`
        : undefined,
    splashBg: template.splash_screen_background_color,
    splashAnimation: template.splash_screen_animation_color,
  };
}

function buildLegacyVariables(
  template: Template,
  fontsReady: boolean,
): BrandingVariables {
  const variables: BrandingVariables = {};

  if (template.brand_primary_color) {
    variables['primary'] = template.brand_primary_color;
  }

  if (template.font_primary_color) {
    variables['font-primary'] = template.font_primary_color;
  }

  if (template.font_secondary_color) {
    variables['font-secondary'] = template.font_secondary_color;
  }

  if (template.background_border_secondary_color) {
    variables['background-secondary'] = template.background_border_secondary_color;
  }

  if (template.background_border_boxes_and_modals_color) {
    const boxes = template.background_border_boxes_and_modals_color;
    variables['boxes-bg'] = boxes;
    variables['modal-background'] = boxes;
  }

  if (template.navigation_background_color) {
    variables['navigation-bg'] = template.navigation_background_color;
  }

  if (template.navigation_menu_items_text_color) {
    variables['navigation-item-text'] = template.navigation_menu_items_text_color;
  }

  if (template.navigation_selected_item_background_color) {
    variables['navigation-selected-bg'] =
      template.navigation_selected_item_background_color;
  }

  if (template.navigation_selected_item_text_color) {
    variables['navigation-selected-text'] = template.navigation_selected_item_text_color;
  }

  if (template.buttons_style) {
    variables['toggle-button-radius'] =
      template.buttons_style === BUTTONS_STYLES_OPTIONS.round ? '50px' : '7px';
  }

  if (fontsReady) {
    if (template.titles_font?.font_name) {
      variables['font-family-titles'] = template.titles_font.font_name;
    }
    if (template.body_font?.font_name) {
      variables['font-family-body'] = template.body_font.font_name;
    }
  }

  return variables;
}

export function buildBrandingVariables(
  template: Template | undefined,
  fontsReady = false,
): BrandingVariables {
  if (!template) {
    return {};
  }

  const variables: BrandingVariables = {
    ...deriveGuestSurface(toSurfaceRoles(template, fontsReady)),
    ...buildLegacyVariables(template, fontsReady),
  };

  return Object.fromEntries(
    Object.entries(variables).filter(([, value]) => Boolean(value)),
  );
}
